import {type PartialOptions, UI} from "../../../stem-core/ui/UIBase.js";
import {registerStyle, styleRule, StyleSheet} from "../../../stem-core/ui/Style";
import {Select} from "../../../stem-core/ui/input/Input.jsx";
import {pluralize} from "../../../stem-core/base/Utils";
import {SearchInput} from "../../ui/input/SearchInput.jsx";
import type {SubscriptionOffer} from "../../../client/state/SubscriptionOfferStore";
import type {UIChild} from "../../../stem-core/ui/UIBase";

// TODO @Mihai merge with JourneySelectInput, only the formatter and the labels differ.


// Offers are referenced by alias in the SDK, so show it next to the name, and the price to tell plans apart.
function formatOffer(offer: SubscriptionOffer) {
    const name = offer.alias ? `${offer.getName()} (${offer.alias})` : offer.getName();
    return `${name} — ${offer.formatBasePrice()}`;
}


class SubscriptionOfferSelectInputStyle extends StyleSheet {
    @styleRule
    matchCount = {
        color: this.themeProps.DASHBOARD_DESCRIPTION_COLOR,
        fontSize: this.themeProps.FONT_SIZE_SMALL,
        marginBottom: 8,
    };

    // Without this the dropdown is as wide as its longest offer name, and spills out of the dialog.
    @styleRule
    select = {
        width: "100%",
    };
}


// An offer dropdown with a search box on top, for merchants that have too many offers to eyeball.
interface SubscriptionOfferSelectInputOptions {
    onChange?: (...args: any[]) => void;
    noMatchMessage?: UIChild;
    offers?: SubscriptionOffer[];
    placeholder?: UIChild;
}

@registerStyle(SubscriptionOfferSelectInputStyle)
export class SubscriptionOfferSelectInput extends UI.Element<SubscriptionOfferSelectInputOptions> {
    declare searchInput: SearchInput;
    declare select: Select<SubscriptionOffer>;

    search = "";

    getDefaultOptions(): PartialOptions<SubscriptionOfferSelectInput> {
        return {
            ...super.getDefaultOptions(),
            offers: [],
            placeholder: "Search by name, alias or price",
            noMatchMessage: "No subscription offer matches your search.",
        };
    }

    getMatchingOffers() {
        const search = this.search.toLowerCase();
        return this.options.offers
            .filter(offer => formatOffer(offer).toLowerCase().includes(search))
            .sort((a, b) => formatOffer(a).localeCompare(formatOffer(b)));
    }

    // Null while the search filters out everything, since then there's no dropdown to select from.
    getValue() {
        return this.getMatchingOffers().length ? this.select.get() : null;
    }

    focus() {
        this.searchInput.focus();
    }

    onSearchChange(search: string) {
        this.search = search;
        this.redraw();
        this.dispatchChange(this.getValue());
    }

    getMatchMessage(matchingOffers: SubscriptionOffer[]) {
        const total = this.options.offers.length;
        if (!matchingOffers.length) {
            return this.options.noMatchMessage;
        }
        if (matchingOffers.length === total) {
            return pluralize(total, "subscription offer");
        }
        return `${matchingOffers.length} of ${pluralize(total, "subscription offer")}`;
    }

    render() {
        const {styleSheet} = this;
        const {placeholder} = this.options;
        const matchingOffers = this.getMatchingOffers();

        return [
            <SearchInput ref="searchInput"
                         inputAttributes={{placeholder}}
                         onChange={(search) => this.onSearchChange(search)}/>,
            <div className={styleSheet.matchCount}>{this.getMatchMessage(matchingOffers)}</div>,
            matchingOffers.length ?
                <Select ref="select"
                        className={styleSheet.select}
                        options={matchingOffers}
                        formatter={formatOffer}
                        onChange={() => this.dispatchChange(this.getValue())}/> :
                null,
        ];
    }
}
